<template>
    <div class="assistent-chat-connect-form">
        <flex-container>
            <flex-container
                :gap="null"
                :padding="Size.md"
                direction="column"
            >
                <text-heading>
                    ChatConnect
                </text-heading>
                <text-description :color="'gray-800'">
                    Zorg voor efficiënte en doelgerichte communicatie. Bepaal interactiedoelen en welke gegevens de AI verzamelt, zodat bezoekers sneller geholpen worden terwijl jij de controle behoudt.
                </text-description>
            </flex-container>

            <template v-if="!systemGoalCollection || systemGoalCollection.loading.value">
                <chat-connect-goal-skeleton
                    v-for="n in 3"
                    :key="n"
                />
            </template>
            <template v-for="systemGoal in systemGoalCollection.data.value" :key="systemGoal.id">
                <flex-container
                    size="full"
                    justify-content="space-between"
                    align-items="center"
                    no-wrap
                    :padding-x="Size.md"
                >
                    <div>
                        <text-heading sub>
                            {{ systemGoal.name }}
                        </text-heading>
                        <text-description>
                            {{ systemGoal.description }}
                        </text-description>
                        <button-text
                            @click="$router.push({ name: 'assistent-chat-edit-chat-connect-system-goals', params: { systemGoal: systemGoal.id.value } })"
                        >
                            <svg-icon
                                :icon="EditIcon"
                                color="primary"
                                :size="IconSize.xs"
                            />
                            <span>Bewerken</span>
                        </button-text>
                    </div>
                    <flex-container size="auto">
                        <input-group
                            v-model="systemGoal.active"
                            :schema="boolean().required()"
                            @input="onSystemGoalToggle(systemGoal, $event.target.value)"
                        >
                            <template #input>
                                <switch-input
                                    :disabled="systemGoalsLoading.includes(systemGoal.id.value)"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>

                <flex-container :padding-x="Size.md">
                    <horizontal-line no-margin />
                </flex-container>
            </template>

            <flex-container :padding-x="Size.md">
                <alert-block :color="'primary'" fill>
                    <template #default>
                        <flex-container
                            justify-content="space-between"
                            align-items="center"
                            no-wrap
                        >
                            <flex-container size="auto">
                                Staat jouw doel er niet tussen of kun je het niet zelf instellen?<br>
                                Laat het ons dan weten, dan helpen we je graag.
                            </flex-container>
                            <flex-container
                                size="auto"
                                justify-content="flex-end"
                            >
                                <button-text @click="requestCustomGoalModal?.openOverlay()">
                                    <text-decorator bold>
                                        Verzoek
                                    </text-decorator>
                                </button-text>
                            </flex-container>
                            <request-new-functionality-modal
                                ref="requestCustomGoalModal"
                                :assistent="assistent"
                                functionality="ChatConnect goal"
                            />
                        </flex-container>
                    </template>
                </alert-block>
            </flex-container>

            <flex-container :padding-x="Size.md" justify-content="space-between">
                <div>
                    <text-heading>
                        Eigen doelen
                    </text-heading>
                </div>
                <div>
                    <button-outline @click="modal?.openOverlay()">
                        Eigen doel toevoegen
                    </button-outline>
                </div>
                <create-chat-connect-goal-modal
                    ref="modal"
                    :assistent="assistent"
                />
            </flex-container>

            <template v-if="!goalCollection || goalCollection.loading.value">
                <chat-connect-goal-skeleton />
            </template>
            <template v-for="goal in goalCollection.data.value" :key="goal.id">
                <flex-container
                    size="full"
                    justify-content="space-between"
                    align-items="center"
                    no-wrap
                    :padding-x="Size.md"
                >
                    <div>
                        <text-heading sub>
                            {{ goal.name }}
                        </text-heading>
                        <text-description>
                            {{ goal.description }}
                        </text-description>
                        <flex-container>
                            <button-text
                                v-if="goal.status.equals(ChatConnectGoalStatus.READY)"
                                @click="$router.push({ name: 'assistent-chat-edit-chat-connect-goal', params: { goal: goal.id.value } })"
                            >
                                <svg-icon
                                    :icon="EditIcon"
                                    :size="IconSize.xs"
                                    color="primary"
                                />
                                <span>Bewerken</span>
                            </button-text>
                            <button-text
                                color="red-500"
                                @click="openRemoveModal(goal)"
                            >
                                <svg-icon
                                    :icon="DeleteIcon"
                                    :size="IconSize.xs"
                                    color="red-500"
                                />
                                <span>Verwijderen</span>
                            </button-text>
                        </flex-container>
                        <text-decorator
                            v-if="goal.status.equals(ChatConnectGoalStatus.GENERATING)"
                            :size="FontSize.xxs"
                            color="gray-500"
                        >
                            <flex-container
                                :gap="Size.xs"
                                align-items="center"
                                inline
                            >
                                Bezig met genereren
                                <span class="goal-generating">
                                    <svg-icon
                                        :icon="LoaderIcon"
                                        :size="IconSize.xxs"
                                        color="primary"
                                        is-custom-icon
                                    />
                                </span>
                            </flex-container>
                        </text-decorator>
                    </div>
                    <flex-container size="auto">
                        <input-group
                            v-model="goal.active"
                            :schema="boolean().required()"
                            @input="onGoalToggle(goal, $event.target.value)"
                        >
                            <template #input>
                                <switch-input
                                    :disabled="goalsLoading.includes(goal.id.value) || goal.status.equals(ChatConnectGoalStatus.GENERATING)"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
                <flex-container :padding-x="Size.md">
                    <horizontal-line no-margin />
                </flex-container>
            </template>

            <template v-if="goalCollection.count.value <= 0">
                <flex-container
                    :gap="Size.xs"
                    justify-content="center"
                    align-items="center"
                    align-content="center"
                    direction="column"
                >
                    <text-heading sub>
                        Er zijn nog geen eigen doelen
                    </text-heading>
                    <text-description>
                        Om een eigen doel toe te voegen, klik op de knop hierboven
                    </text-description>
                </flex-container>
            </template>
        </flex-container>

        <flex-container :padding-x="Size.md">
            &nbsp;
        </flex-container>

        <confirmation-dialog
            ref="removeConfirmationModal"
            label="Doel Verwijderen"
            description="Weet je zeker dat je dit doel wilt verwijderen?"
            hide-close
        >
            <template #actions>
                <button-text
                    @click="closeRemoveModal"
                >
                    Annuleren
                </button-text>
                <button-block @loading="remove($event)">
                    Verwijderen
                </button-block>
            </template>
        </confirmation-dialog>
    </div>
</template>

<script setup lang="ts">
    import {
        ButtonState,
        type ButtonStateChangedEvent,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        HorizontalLine,
        IconSize,
        InputGroup,
        NotificationBlock,
        Size,
        SwitchInput,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';

    import DeleteIcon from '@keyvue/one-material-icons/icons/delete.svg?component';
    import EditIcon from '@keyvue/one-material-icons/icons/edit.svg?component';
    import LoaderIcon from '@keyvue/one-awesome-icons/icons/regular/loader.svg?component';
    import { onMounted, type Ref, ref, watch } from 'vue';
    import { ActivateGoalCommand, DeactivateSystemGoalCommand, ActivateSystemGoalCommand, type Assistent, ChatConnectGoal, ChatConnectGoalStatus, DeactivateGoalCommand, ChatConnectSystemGoal, DeleteGoalCommand } from '@keyvue/one-api-client';
    import { boolean } from 'yup';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import CreateChatConnectGoalModal from '@/ai/assistent/components/forms/chat-connect/CreateChatConnectGoalModal.vue';
    import ChatConnectGoalSkeleton from '@/ai/assistent/components/skeleton/ChatConnectGoalSkeleton.vue';
    import RequestNewFunctionalityModal from '@/ai/assistent/components/forms/RequestNewFunctionalityModal.vue';

    const modal = ref<ExposedOverlay>();
    const removeConfirmationModal = ref<ExposedOverlay>();
    const requestCustomGoalModal = ref<ExposedOverlay>();

    const goalToBeDeleted = ref<ChatConnectGoal | null>(null);

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const { client, getLazySubResourceCollection } = useClient();
    const { notify } = useNotify();
    const assistent = ref(props.modelValue) as Ref<Assistent>;

    const goalCollection = getLazySubResourceCollection(
        assistent.value,
        ChatConnectGoal,
        'goals',
        { pagination: false },
    );
    const systemGoalCollection = getLazySubResourceCollection(
        assistent.value,
        ChatConnectSystemGoal,
        'system_goals',
        { pagination: false },
    );
    const goalsLoading = ref<string[]>([]);
    const systemGoalsLoading = ref<string[]>([]);

    onMounted(async () => {
        await Promise.all([
            goalCollection.load(),
            systemGoalCollection.load(),
        ]);
    });

    async function onSystemGoalToggle(systemGoal: ChatConnectSystemGoal, value: boolean): Promise<void> {
        systemGoalsLoading.value.push(systemGoal.id.value);

        if (systemGoal.active && !value) {
            await deactivateSystemGoal(systemGoal);
        }

        if (!systemGoal.active && value) {
            await activateSystemGoal(systemGoal);
        }

        systemGoalsLoading.value.splice(systemGoalsLoading.value.indexOf(systemGoal.id.value), 1);
    }

    async function onGoalToggle(goal: ChatConnectGoal, value: boolean): Promise<void> {
        goalsLoading.value.push(goal.id.value);

        if (goal.active && !value) {
            await deactivateGoal(goal);
        }

        if (!goal.active && value) {
            await activateGoal(goal);
        }

        goalsLoading.value.splice(goalsLoading.value.indexOf(goal.id.value), 1);
    }

    async function deactivateSystemGoal(systemGoal: ChatConnectSystemGoal): Promise<void> {
        try {
            await client.dispatch(DeactivateSystemGoalCommand.fromGoal(systemGoal));
            notifyGoalSucces('We hebben het systeem doel gedeactiveerd.');
        } catch {
            systemGoal.changeActive(true);
            notifyGoalFail('Er is iets fout gegaan tijdens het deactiveren van het systeem doel.');
        }
    }

    async function activateSystemGoal(systemGoal: ChatConnectSystemGoal): Promise<void> {
        try {
            await client.dispatch(ActivateSystemGoalCommand.fromGoal(systemGoal));
            notifyGoalSucces('We hebben het systeem doel geactiveerd.');
        } catch {
            systemGoal.changeActive(false);
            notifyGoalFail('Er is iets fout gegaan tijdens het activeren van het systeem doel.');
        }
    }

    async function deactivateGoal(goal: ChatConnectGoal): Promise<void> {
        try {
            await client.dispatch(DeactivateGoalCommand.fromGoal(goal));
            notifyGoalSucces('We hebben het doel gedeactiveerd.');
        } catch {
            goal.changeActive(true);
            notifyGoalFail('Er is iets fout gegaan tijdens het deactiveren van het doel.');
        }
    }

    async function activateGoal(goal: ChatConnectGoal): Promise<void> {
        try {
            await client.dispatch(ActivateGoalCommand.fromGoal(goal));
            notifyGoalSucces('We hebben het doel geactiveerd.');
        } catch {
            goal.changeActive(false);
            notifyGoalFail('Er is iets fout gegaan tijdens het activeren van het doel.');
        }
    }

    function openRemoveModal(goal: ChatConnectGoal) {
        removeConfirmationModal.value?.openOverlay();
        goalToBeDeleted.value = goal;
    }
    function closeRemoveModal() {
        removeConfirmationModal.value?.closeOverlay();
        goalToBeDeleted.value = null;
    }

    async function remove(event: ButtonStateChangedEvent) {
        if (goalToBeDeleted.value === null) {
            return;
        }

        try {
            await client.dispatch(DeleteGoalCommand.fromGoal(goalToBeDeleted.value as ChatConnectGoal));
            await goalCollection.load();
            event.button.setState(ButtonState.completed);
            removeConfirmationModal.value?.closeOverlay();
            notifyGoalSucces('Het is gelukt om het doel te verwijderen.');
        } catch (e) {
            notifyGoalFail('Er is iets misgegaan met het doel verwijderen, probeer het later opnieuw.');
        }
    }

    function notifyGoalSucces(reason: string) {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: reason,
                color: 'green-500',
            },
            duration: 2000,
        });
    }

    function notifyGoalFail(reason: string) {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Mislukt!',
                text: reason,
                color: 'red-500',
            },
            duration: 4000,
        });
    }

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });
</script>

<style scoped lang="scss">
    @keyframes rotating {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    .goal-generating {
        width:12px;
        height:12px;
        animation: rotating 2s linear infinite;
        transform-origin: center;
    }
</style>
